import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import {
  ArrowUpFromLine, ArrowDownToLine, Plus, Search, CheckCheck
} from "lucide-react";
import { FISCAL_YEARS } from "@/constants/modules";
import { COMM_TYPES } from "./communications/commHelpers";
import SendForm from "./communications/SendForm";
import CommList from "./communications/CommList";
import { useReadOverrides } from "@/hooks/useReadOverrides";

/**
 * Module Communications (Documents) — modes :
 *  - mode="sent"        : focus envois uniquement (onglet received cache)
 *  - mode="received"    : focus depots uniquement (onglet sent cache)
 *  - mode=undefined     : les 2 onglets visibles
 * Le flow signature est géré via /admin/client-documents (Documents à signer).
 */
export default function CommunicationsModule({ mode } = {}) {
  const [searchParams] = useSearchParams();
  const initialTab = mode === "received" ? "received" : "sent";
  const [tab, setTab] = useState(initialTab);
  const [comms, setComms] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSendForm, setShowSendForm] = useState(false);
  const [filterClient, setFilterClient] = useState("");
  const [filterType, setFilterType] = useState(searchParams.get("type") || "");
  const [filterYear, setFilterYear] = useState("");
  const { isOverridden, markRead, markUnread, markAllRead, resetAll, count: overrideCount } = useReadOverrides("communication");

  // Force tab consistency when mode changes (route navigation)
  useEffect(() => {
    if (mode === "received") setTab("received");
    else if (mode === "sent") setTab("sent");
  }, [mode]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterYear) params.tax_year = filterYear;
      const [commsRes, clientsRes] = await Promise.all([
        api.get("/communications", { params }),
        api.get("/clients"),
      ]);
      const clientMap = {};
      (clientsRes.data || []).forEach(c => { clientMap[c.id] = `${c.first_name} ${c.last_name}`; });
      const enriched = (commsRes.data || []).map(c => ({
        ...c,
        _client_name: clientMap[c.client_id] || "",
      }));
      setComms(enriched);
      setClients(clientsRes.data || []);
    } catch {
      toast.error("Erreur chargement");
    } finally {
      setLoading(false);
    }
  }, [filterYear]);

  useEffect(() => { load(); }, [load]);

  const filterFn = useCallback(
    (c) =>
      (!filterClient || c._client_name.toLowerCase().includes(filterClient.toLowerCase())) &&
      (!filterType || c.type === filterType),
    [filterClient, filterType],
  );

  const sentItems = useMemo(
    () => comms.filter(c => c.direction === "sent" && filterFn(c)),
    [comms, filterFn],
  );
  const receivedItems = useMemo(
    () => comms.filter(c => c.direction === "received" && filterFn(c)),
    [comms, filterFn],
  );

  const tabs = [
    { id: "sent", label: "Envoye", icon: ArrowUpFromLine, count: comms.filter(c => c.direction === "sent").length },
    { id: "received", label: "Recu", icon: ArrowDownToLine, count: comms.filter(c => c.direction === "received").length },
  ];
  // n'afficher que les onglets pertinents quand un `mode` est impose
  const visibleTabs = mode === "received"
    ? tabs.filter(t => t.id === "received")
    : mode === "sent"
      ? tabs.filter(t => t.id === "sent")
      : tabs;

  const titles = {
    sent: "Envois",
    received: "Dépôts",
  };
  const headerTitle = mode ? titles[mode] : "Communications";

  return (
    <div className="space-y-4" data-testid="communications-module" data-mode={mode || "default"}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-xl font-bold text-gray-900" data-testid="module-title">{headerTitle}</h1>
        <div className="flex items-center gap-2 flex-wrap">
          {(() => {
            const itemsForTab = tab === "sent" ? sentItems : receivedItems;
            const unreadIds = itemsForTab.filter(c => !c.read_at && !isOverridden(c.id)).map(c => c.id);
            if (unreadIds.length === 0 && overrideCount === 0) return null;
            return (
              <>
                {unreadIds.length > 0 && (
                  <button
                    onClick={async () => {
                      if (window.confirm(`Marquer comme lu ${unreadIds.length} communication(s) ?`)) {
                        await markAllRead(unreadIds);
                      }
                    }}
                    className="text-xs px-2.5 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-1.5"
                    data-testid="comm-mark-all-read"
                  >
                    <CheckCheck className="w-3.5 h-3.5" />
                    Tout marquer lu ({unreadIds.length})
                  </button>
                )}
                {overrideCount > 0 && (
                  <button
                    onClick={() => {
                      if (window.confirm(`Annuler les ${overrideCount} marquage(s) manuel(s) ?`)) resetAll();
                    }}
                    className="text-xs px-2.5 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50"
                    data-testid="comm-reset-overrides"
                  >
                    Annuler marquages ({overrideCount})
                  </button>
                )}
              </>
            );
          })()}
          {tab === "sent" && (
            <button onClick={() => setShowSendForm(true)}
              className="flex items-center gap-2 bg-[#072354] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#0a2d6e] transition"
              data-testid="new-send-btn">
              <Plus className="w-4 h-4" /> Nouvel envoi
            </button>
          )}
        </div>
      </div>

      {/* Tabs (only render when more than one is visible) */}
      {visibleTabs.length > 1 && (
        <div className="flex gap-1 border-b overflow-x-auto" data-testid="comm-tabs">
          {visibleTabs.map(t => {
            const active = tab === t.id;
            return (
              <button key={t.id}
                onClick={() => { setTab(t.id); setShowSendForm(false); }}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition whitespace-nowrap ${active ? "border-[#072354] text-[#072354]" : "border-transparent text-gray-500 hover:text-gray-700"}`}
                data-testid={`tab-${t.id}`}>
                <t.icon className="w-4 h-4" />
                {t.label}
                {t.count > 0 && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${active ? "bg-[#072354] text-white" : "bg-gray-100 text-gray-500"}`}>
                    {t.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" placeholder="Filtrer par client..." value={filterClient}
            onChange={e => setFilterClient(e.target.value)}
            className="w-full border rounded-lg pl-9 pr-3 py-2 text-sm" data-testid="filter-client" />
        </div>
        <select value={filterType} onChange={e => setFilterType(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm" data-testid="filter-type">
          <option value="">Tous les types</option>
          {COMM_TYPES.map(ct => <option key={ct.id} value={ct.id}>{ct.label}</option>)}
        </select>
        <select value={filterYear} onChange={e => setFilterYear(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm" data-testid="filter-year">
          <option value="">Toutes annees</option>
          {FISCAL_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <button
          onClick={() => { setFilterClient(""); setFilterType(""); setFilterYear(""); }}
          disabled={!(filterClient || filterType || filterYear)}
          className="text-xs text-[#072354] hover:underline disabled:text-gray-300 disabled:no-underline disabled:cursor-not-allowed"
          data-testid="reset-filters">
          Reinitialiser
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12" data-testid="comm-loading">
          <div className="w-8 h-8 border-3 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {showSendForm && tab === "sent" && (
            <SendForm clients={clients}
              onSend={() => { setShowSendForm(false); load(); }}
              onCancel={() => setShowSendForm(false)} />
          )}

          {tab === "sent" && !showSendForm && (
            <CommList
              items={sentItems}
              direction="sent"
              onRefresh={load}
              isOverridden={isOverridden}
              markRead={markRead}
              markUnread={markUnread}
            />
          )}

          {tab === "received" && (
            <CommList
              items={receivedItems}
              direction="received"
              onRefresh={load}
              isOverridden={isOverridden}
              markRead={markRead}
              markUnread={markUnread}
            />
          )}
        </>
      )}

    </div>
  );
}
